/**
 * World.ts — Environment generation.
 *
 * Procedurally builds:
 *   - Height-map terrain (mountains, valleys, lakes)
 *   - Low-poly trees and forests
 *   - Airstrips with runway markings
 *   - Water planes for lakes
 *   - Sky dome / gradient background
 *   - Ambient, directional, and hemisphere lights
 *   - Day/night cycle colouring
 *   - Fuel pickups scattered in the world
 *   - Storm clouds to avoid
 */
import * as THREE from 'three';
/* ---- helpers ---- */
function seededRandom(seed) {
    let s = seed;
    return () => { s = (s * 16807 + 0) % 2147483647; return (s - 1) / 2147483646; };
}
const rng = seededRandom(42);
/* simplex-ish noise (value noise with smoothstep, good enough for low-poly) */
function hash2(x, z) {
    let n = Math.sin(x * 127.1 + z * 311.7) * 43758.5453123;
    return n - Math.floor(n);
}
function smoothNoise(x, z) {
    const ix = Math.floor(x), iz = Math.floor(z);
    const fx = x - ix, fz = z - iz;
    const sx = fx * fx * (3 - 2 * fx), sz = fz * fz * (3 - 2 * fz);
    const a = hash2(ix, iz), b = hash2(ix + 1, iz);
    const c = hash2(ix, iz + 1), d = hash2(ix + 1, iz + 1);
    return a + (b - a) * sx + (c - a) * sz + (a - b - c + d) * sx * sz;
}
function fbm(x, z, octaves, lacunarity, gain) {
    let val = 0, amp = 1, freq = 1, tot = 0;
    for (let i = 0; i < octaves; i++) {
        val += smoothNoise(x * freq, z * freq) * amp;
        tot += amp;
        amp *= gain;
        freq *= lacunarity;
    }
    return val / tot;
}
/* ========================================================================= */
/*  World class                                                              */
/* ========================================================================= */
export class World {
    constructor(game) {
        /* Terrain data */
        this.terrainSize = 5000; // total width & depth
        this.segments = 256;
        /* Environment objects */
        this.airstrips = [];
        this.airstripLabels = [];
        this.fuelPickups = [];
        this.stormClouds = [];
        this.landmarks = [];
        this.game = game;
        this.heightData = new Float32Array((this.segments + 1) * (this.segments + 1));
        this.buildTerrain();
        this.buildWater();
        this.buildTrees();
        this.buildAirstrips();
        this.buildFuelPickups();
        this.buildStormClouds();
        this.buildLandmarks();
        this.buildLights();
        this.buildSky();
    }
    /* ===== TERRAIN ===== */
    buildTerrain() {
        const seg = this.segments;
        const half = this.terrainSize / 2;
        const geo = new THREE.PlaneGeometry(this.terrainSize, this.terrainSize, seg, seg);
        geo.rotateX(-Math.PI / 2);
        const pos = geo.attributes.position;
        const colors = new Float32Array(pos.count * 3);
        const color = new THREE.Color();
        for (let i = 0; i < pos.count; i++) {
            const x = pos.getX(i);
            const z = pos.getZ(i);
            /* Multi-octave FBM for interesting terrain */
            let h = fbm(x / 800, z / 800, 6, 2.0, 0.48) * 350;
            /* Big mountain ridge */
            h += Math.max(0, 180 - Math.abs(x - 400) * 0.35) * fbm(x / 200, z / 200, 3, 2, 0.5);
            /* Valley / lake depressions */
            const lake1 = Math.max(0, 1 - ((x + 500) ** 2 + (z - 200) ** 2) / 300000);
            const lake2 = Math.max(0, 1 - ((x - 800) ** 2 + (z + 700) ** 2) / 200000);
            h -= lake1 * 60;
            h -= lake2 * 50;
            /* Flatten airstrip zones */
            const flat1 = Math.max(0, 1 - ((x) ** 2 + (z) ** 2) / 60000); // origin strip
            const flat2 = Math.max(0, 1 - ((x + 900) ** 2 + (z - 900) ** 2) / 50000); // NW strip
            const flat3 = Math.max(0, 1 - ((x - 700) ** 2 + (z + 600) ** 2) / 50000); // SE strip
            if (flat1 > 0.3)
                h = h * (1 - flat1) + 2 * flat1;
            if (flat2 > 0.3)
                h = h * (1 - flat2) + 35 * flat2;
            if (flat3 > 0.3)
                h = h * (1 - flat3) + 12 * flat3;
            pos.setY(i, h);
            this.heightData[i] = h;
            /* Colour terrain by height */
            if (h < 2)
                color.setHex(0x967957); // sandy shore
            else if (h < 30)
                color.setHex(0x5a8f3c); // grass
            else if (h < 100)
                color.setHex(0x4a7a32); // deeper green
            else if (h < 200)
                color.setHex(0x8a8a7a); // rock
            else
                color.setHex(0xddeeff); // snow
            /* Slight random variation */
            color.r += (rng() - 0.5) * 0.07;
            color.g += (rng() - 0.5) * 0.07;
            color.b += (rng() - 0.5) * 0.07;
            colors[i * 3] = color.r;
            colors[i * 3 + 1] = color.g;
            colors[i * 3 + 2] = color.b;
        }
        geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        geo.computeVertexNormals();
        const mat = new THREE.MeshStandardMaterial({
            vertexColors: true,
            flatShading: true,
            roughness: 0.85,
            metalness: 0.05,
        });
        this.terrainMesh = new THREE.Mesh(geo, mat);
        this.terrainMesh.receiveShadow = true;
        this.game.scene.add(this.terrainMesh);
    }
    /* ===== WATER ===== */
    buildWater() {
        const waterGeo = new THREE.PlaneGeometry(this.terrainSize, this.terrainSize);
        waterGeo.rotateX(-Math.PI / 2);
        const waterMat = new THREE.MeshStandardMaterial({
            color: 0x2288aa,
            transparent: true,
            opacity: 0.55,
            roughness: 0.1,
            metalness: 0.3,
            flatShading: true,
        });
        const water = new THREE.Mesh(waterGeo, waterMat);
        water.position.y = -2;
        water.receiveShadow = true;
        this.game.scene.add(water);
    }
    /* ===== TREES ===== */
    buildTrees() {
        const trunkGeo = new THREE.CylinderGeometry(0.3, 0.5, 3, 5);
        const leavesGeo = new THREE.ConeGeometry(2.5, 5, 6);
        const trunkMat = new THREE.MeshStandardMaterial({ color: 0x8B5A2B, flatShading: true });
        const leavesMat = new THREE.MeshStandardMaterial({ color: 0x3a7a2a, flatShading: true });
        const treeProto = new THREE.Group();
        const trunk = new THREE.Mesh(trunkGeo, trunkMat);
        trunk.position.y = 1.5;
        const leaves = new THREE.Mesh(leavesGeo, leavesMat);
        leaves.position.y = 5;
        treeProto.add(trunk, leaves);
        const instanceCount = 1200;
        const half = this.terrainSize / 2 - 100;
        // We'll use merged geometry for performance
        const treeMeshes = [];
        for (let i = 0; i < instanceCount; i++) {
            const x = (rng() - 0.5) * half * 2;
            const z = (rng() - 0.5) * half * 2;
            const h = this.getTerrainHeight(x, z);
            // Only place trees on grassy terrain
            if (h < 4 || h > 80)
                continue;
            const t = treeProto.clone();
            const scale = 0.8 + rng() * 1.2;
            t.scale.set(scale, scale, scale);
            t.position.set(x, h, z);
            t.rotation.y = rng() * Math.PI * 2;
            treeMeshes.push(t);
        }
        // Group them all under one parent for easy management
        const forest = new THREE.Group();
        forest.name = 'forest';
        treeMeshes.forEach(t => forest.add(t));
        forest.traverse(c => { if (c.isMesh) {
            c.castShadow = true;
            c.receiveShadow = true;
        } });
        this.game.scene.add(forest);
    }
    /* ===== AIRSTRIPS ===== */
    buildAirstrips() {
        const strips = [
            { x: 0, z: 0, heading: 0, name: 'Home Field' },
            { x: -900, z: 900, heading: Math.PI / 6, name: 'Eagle Ridge' },
            { x: 700, z: -600, heading: -Math.PI / 4, name: 'Pine Valley' },
        ];
        const runwayMat = new THREE.MeshStandardMaterial({ color: 0x555555, flatShading: true });
        const markMat = new THREE.MeshStandardMaterial({ color: 0xffffff, flatShading: true });
        for (const s of strips) {
            const terrainY = this.getTerrainHeight(s.x, s.z);
            const stripPos = new THREE.Vector3(s.x, terrainY + 0.2, s.z);
            const group = new THREE.Group();
            group.position.copy(stripPos);
            group.rotation.y = s.heading;
            // Runway surface
            const runway = new THREE.Mesh(new THREE.BoxGeometry(8, 0.15, 80), runwayMat);
            group.add(runway);
            // Centre-line markings
            for (let z = -35; z <= 35; z += 8) {
                const mark = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.2, 3), markMat);
                mark.position.set(0, 0.08, z);
                group.add(mark);
            }
            // Threshold markings
            for (const end of [-38, 38]) {
                for (let x = -3; x <= 3; x += 1.5) {
                    const m = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.2, 2), markMat);
                    m.position.set(x, 0.08, end);
                    group.add(m);
                }
            }
            // Windsock pole
            const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, 4, 4), markMat);
            pole.position.set(6, 2, -30);
            group.add(pole);
            const sock = new THREE.Mesh(new THREE.ConeGeometry(0.3, 1.5, 4), new THREE.MeshStandardMaterial({ color: 0xff6600, flatShading: true }));
            sock.rotation.z = Math.PI / 2;
            sock.position.set(6, 4, -30);
            group.add(sock);
            group.traverse(c => { if (c.isMesh) {
                c.receiveShadow = true;
            } });
            this.game.scene.add(group);
            this.airstrips.push({ position: stripPos.clone(), heading: s.heading, name: s.name });
            const beacon = this.createLandingZoneBeacon(stripPos);
            this.game.scene.add(beacon);
            const label = this.createAirstripLabel(s.name);
            label.position.set(stripPos.x, stripPos.y + 380, stripPos.z);
            this.airstripLabels.push(label);
            this.game.scene.add(label);
        }
    }
    createLandingZoneBeacon(stripPos) {
        const beacon = new THREE.Group();
        const topY = 360;
        const beam = new THREE.Mesh(new THREE.CylinderGeometry(3.2, 3.2, topY, 10), new THREE.MeshBasicMaterial({ color: 0x6dc3ff, transparent: true, opacity: 0.24, depthWrite: false, depthTest: false }));
        beam.position.set(stripPos.x, stripPos.y + topY * 0.5, stripPos.z);
        const halo = new THREE.Mesh(new THREE.TorusGeometry(22, 2.2, 8, 26), new THREE.MeshBasicMaterial({ color: 0x9ad8ff, transparent: true, opacity: 0.95, depthWrite: false, depthTest: false }));
        halo.rotation.x = Math.PI / 2;
        halo.position.set(stripPos.x, stripPos.y + topY, stripPos.z);
        const core = new THREE.Mesh(new THREE.SphereGeometry(4, 8, 8), new THREE.MeshBasicMaterial({ color: 0xd7f0ff, depthWrite: false, depthTest: false }));
        core.position.set(stripPos.x, stripPos.y + topY, stripPos.z);
        beacon.add(beam, halo, core);
        return beacon;
    }
    createAirstripLabel(name) {
        const canvas = document.createElement('canvas');
        canvas.width = 512;
        canvas.height = 128;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = 'rgba(10, 20, 40, 0.72)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.strokeStyle = 'rgba(126, 179, 255, 0.9)';
        ctx.lineWidth = 4;
        ctx.strokeRect(6, 6, canvas.width - 12, canvas.height - 12);
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 44px Segoe UI';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(name.toUpperCase(), canvas.width / 2, canvas.height / 2 + 2);
        const tex = new THREE.CanvasTexture(canvas);
        tex.needsUpdate = true;
        const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false });
        const sprite = new THREE.Sprite(mat);
        sprite.scale.set(90, 22, 1);
        return sprite;
    }
    /* ===== FUEL PICKUPS ===== */
    buildFuelPickups() {
        const geo = new THREE.OctahedronGeometry(2, 0);
        const mat = new THREE.MeshStandardMaterial({ color: 0xffcc00, emissive: 0xffaa00, emissiveIntensity: 0.6, flatShading: true });
        const half = 1800;
        for (let i = 0; i < 20; i++) {
            const x = (rng() - 0.5) * half * 2;
            const z = (rng() - 0.5) * half * 2;
            const h = this.getTerrainHeight(x, z);
            const mesh = new THREE.Mesh(geo, mat.clone());
            mesh.position.set(x, Math.max(h + 15, 40 + rng() * 100), z);
            this.game.scene.add(mesh);
            this.fuelPickups.push({ mesh, collected: false });
        }
    }
    /* ===== STORM CLOUDS ===== */
    buildStormClouds() {
        for (let i = 0; i < 6; i++) {
            const cx = (rng() - 0.5) * 3000;
            const cz = (rng() - 0.5) * 3000;
            const cy = 150 + rng() * 200;
            const radius = 80 + rng() * 100;
            const group = new THREE.Mesh(new THREE.SphereGeometry(radius, 7, 5), new THREE.MeshStandardMaterial({ color: 0x334455, transparent: true, opacity: 0.5, flatShading: true }));
            group.position.set(cx, cy, cz);
            // Smaller cloud puffs
            for (let j = 0; j < 5; j++) {
                const puff = new THREE.Mesh(new THREE.SphereGeometry(radius * (0.4 + rng() * 0.4), 6, 4), new THREE.MeshStandardMaterial({ color: 0x445566, transparent: true, opacity: 0.35, flatShading: true }));
                puff.position.set((rng() - 0.5) * radius, (rng() - 0.5) * radius * 0.4, (rng() - 0.5) * radius);
                group.add(puff);
            }
            this.game.scene.add(group);
            this.stormClouds.push({ mesh: group, center: new THREE.Vector3(cx, cy, cz), radius });
        }
    }
    /* ===== LANDMARKS ===== */
    buildLandmarks() {
        const beaconMat = new THREE.MeshStandardMaterial({ color: 0xff4444, emissive: 0xff2222, emissiveIntensity: 0.8, flatShading: true });
        const spots = [
            new THREE.Vector3(400, 0, -1000),
            new THREE.Vector3(-1200, 0, -500),
            new THREE.Vector3(1100, 0, 800),
            new THREE.Vector3(-600, 0, 1400),
            new THREE.Vector3(200, 0, 1800),
        ];
        for (const sp of spots) {
            const h = this.getTerrainHeight(sp.x, sp.z);
            sp.y = h;
            // A little beacon tower
            const base = new THREE.Mesh(new THREE.CylinderGeometry(1.5, 2, 8, 6), beaconMat);
            base.position.set(sp.x, h + 4, sp.z);
            const top = new THREE.Mesh(new THREE.SphereGeometry(2.5, 6, 4), beaconMat);
            top.position.set(sp.x, h + 10, sp.z);
            this.game.scene.add(base, top);
            this.landmarks.push(sp.clone());
        }
    }
    /* ===== LIGHTS ===== */
    buildLights() {
        this.sunLight = new THREE.DirectionalLight(0xffeedd, 1.5);
        this.sunLight.position.set(300, 500, 200);
        this.sunLight.castShadow = true;
        this.sunLight.shadow.mapSize.set(2048, 2048);
        this.sunLight.shadow.camera.left = -500;
        this.sunLight.shadow.camera.right = 500;
        this.sunLight.shadow.camera.top = 500;
        this.sunLight.shadow.camera.bottom = -500;
        this.sunLight.shadow.camera.near = 1;
        this.sunLight.shadow.camera.far = 2000;
        this.game.scene.add(this.sunLight);
        this.hemiLight = new THREE.HemisphereLight(0x88bbff, 0x445522, 0.5);
        this.game.scene.add(this.hemiLight);
        this.ambientLight = new THREE.AmbientLight(0x334466, 0.3);
        this.game.scene.add(this.ambientLight);
    }
    /* ===== SKY ===== */
    buildSky() {
        const skyGeo = new THREE.SphereGeometry(5000, 16, 16);
        const skyMat = new THREE.MeshBasicMaterial({
            color: 0x88bbee,
            side: THREE.BackSide,
        });
        const sky = new THREE.Mesh(skyGeo, skyMat);
        sky.name = 'sky';
        this.game.scene.add(sky);
    }
    /* ===== DAY / NIGHT ===== */
    updateDayNight(t) {
        // t: 0 = midnight, 0.25 = sunrise, 0.5 = noon, 0.75 = sunset
        const sunAngle = t * Math.PI * 2 - Math.PI / 2;
        const sy = Math.sin(sunAngle);
        const sx = Math.cos(sunAngle) * 300;
        const sz = 200;
        this.sunLight.position.set(sx, Math.max(sy, 0.05) * 500, sz);
        const dayFactor = THREE.MathUtils.clamp(sy + 0.3, 0, 1);
        this.sunLight.intensity = dayFactor * 1.8;
        this.ambientLight.intensity = 0.08 + dayFactor * 0.3;
        this.hemiLight.intensity = 0.1 + dayFactor * 0.5;
        // Sky colour
        const sky = this.game.scene.getObjectByName('sky');
        if (sky) {
            const c = new THREE.Color();
            c.setHSL(0.58, 0.5, 0.15 + dayFactor * 0.55);
            sky.material.color.copy(c);
        }
        // Fog
        const fog = this.game.scene.fog;
        const fogC = new THREE.Color();
        fogC.setHSL(0.58, 0.35, 0.2 + dayFactor * 0.5);
        fog.color.copy(fogC);
        this.game.renderer.setClearColor(fogC);
    }
    /* ===== TERRAIN HEIGHT LOOKUP ===== */
    getTerrainHeight(wx, wz) {
        const half = this.terrainSize / 2;
        const step = this.terrainSize / this.segments;
        // Convert world coords to grid indices
        const gx = (wx + half) / step;
        const gz = (wz + half) / step;
        const ix = Math.floor(THREE.MathUtils.clamp(gx, 0, this.segments - 1));
        const iz = Math.floor(THREE.MathUtils.clamp(gz, 0, this.segments - 1));
        const fx = gx - ix;
        const fz = gz - iz;
        const stride = this.segments + 1;
        const h00 = this.heightData[iz * stride + ix];
        const h10 = this.heightData[iz * stride + ix + 1];
        const h01 = this.heightData[(iz + 1) * stride + ix];
        const h11 = this.heightData[(iz + 1) * stride + ix + 1];
        // Bilinear interpolation
        return h00 * (1 - fx) * (1 - fz) + h10 * fx * (1 - fz) + h01 * (1 - fx) * fz + h11 * fx * fz;
    }
    /* ===== PER-FRAME UPDATE ===== */
    update(dt) {
        /* Animate fuel pickups (spin + bob) */
        const time = this.game.clock.elapsedTime;
        for (const fp of this.fuelPickups) {
            if (fp.collected)
                continue;
            fp.mesh.rotation.y += dt * 1.5;
            fp.mesh.position.y += Math.sin(time * 2 + fp.mesh.position.x) * 0.02;
            // Pickup detection
            const d = fp.mesh.position.distanceTo(this.game.player.mesh.position);
            if (d < 12) {
                fp.collected = true;
                fp.mesh.visible = false;
                this.game.refuel(20);
                this.game.addScore(50, '+20 Fuel!');
            }
        }
        /* Storm cloud hazard */
        for (const sc of this.stormClouds) {
            const d = sc.center.distanceTo(this.game.player.mesh.position);
            if (d < sc.radius * 0.8) {
                // Turbulence: shake the plane
                this.game.player.velocity.x += (Math.random() - 0.5) * 80 * dt;
                this.game.player.velocity.y += (Math.random() - 0.5) * 60 * dt;
                this.game.player.velocity.z += (Math.random() - 0.5) * 80 * dt;
                // Drain fuel faster
                this.game.fuel -= dt * 1.5;
            }
        }
        /* Move the directional-light shadow camera to follow the player
           (so shadows are always visible near the plane) */
        const pp = this.game.player.mesh.position;
        this.sunLight.target.position.set(pp.x, 0, pp.z);
        this.sunLight.target.updateMatrixWorld();
        const sOff = this.sunLight.position.clone().sub(new THREE.Vector3(pp.x, 0, pp.z)).normalize().multiplyScalar(600);
        this.sunLight.position.set(pp.x + sOff.x, Math.abs(sOff.y) + 100, pp.z + sOff.z);
        /* Keep airfield labels readable by scaling with distance. */
        const cameraPos = this.game.camera.position;
        for (const label of this.airstripLabels) {
            const d = label.position.distanceTo(cameraPos);
            const s = THREE.MathUtils.clamp(d * 0.06, 70, 160);
            label.scale.set(s, s * 0.24, 1);
        }
    }
}
